import React, { useEffect, useRef, useState } from "react";
import { useToast } from "../context/ToastContext";
import "./ToolPage.css";
import flaskApi from "../services/flaskApi";
import { extractFilenameFromHeader } from "../utils/fileUtils";
import { extractErrorMessage } from "../utils/errorUtils";
import ToolInfoModal from "../components/common/ToolInfoModal";
import ToolFileUploadField from "./shared/ToolFileUploadField";
import ToolPageLayout from "./shared/ToolPageLayout";
import ToolProgressPanel from "./shared/ToolProgressPanel";
import ToolJobsSection from "./shared/ToolJobsSection";
import JobInputActions from "./shared/JobInputActions";
import useJobsPagination from "./shared/useJobsPagination";


const STATUS_SCHEDULED = "scheduled";
const STATUS_DONE = "done";
const STATUS_FAILED = "failed";
const STATUS_CANCELLED = "cancelled";

// Must match sale_flags_updater.py's ACTIONS.
const ACTION_REMOVE = "remove";
const ACTION_ADD = "add";

const RUN_NOW = "now";
const RUN_SCHEDULE = "schedule";

const ENDPOINT = "/shopify/sale-flags-update";

// The flag the storefront's final-sale checkout checkbox looks for. Pre-filled
// because it is overwhelmingly the value this tool is used with.
const DEFAULT_FLAG = "final-sale";

// Identifier the backend should key on. Values must match the backend's
// IDENTIFIER_COLUMNS in product_tag_adder.py; "" is the auto-cascade.
const IDENTIFIER_OPTIONS = [
  { value: "", label: "Auto (try SKU, then Product ID, then Handle)" },
  { value: "handle", label: "HANDLE — recommended" },
  { value: "sku", label: "SKU / Parent SKU / Variant SKU" },
  { value: "shopify_product_id", label: "SHOPIFY_PRODUCT_ID" },
  { value: "title", label: "Product Title" },
];

const IDENTIFIER_HELPER = {
  "": "Tries SKU, then SHOPIFY_PRODUCT_ID, then HANDLE — whichever columns your file has.",
  handle:
    "Handle is the best key here: the metafield is per product, and a handle always identifies exactly one product.",
  sku:
    "SKU search is a prefix match, so a short parent SKU can resolve to a different product. Check the report's Matched Title column before running live.",
  shopify_product_id: "Exact match on the Shopify product GID or numeric ID.",
  title: "Exact product title, case-insensitive. Less precise than handle.",
};

// "YYYY-MM-DDTHH:mm" in the user's local time — what <input type="datetime-local"> expects.
const formatLocalDatetimeInput = (date) => {
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
};

const formatScheduledDisplay = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const ACTION_LABEL = {
  [ACTION_REMOVE]: "Remove",
  [ACTION_ADD]: "Add",
};


function SaleFlagsUpdateTool() {
  const [inputFile, setInputFile] = useState(null);
  const [sheetName, setSheetName] = useState("");
  const [action, setAction] = useState(ACTION_REMOVE);
  const [flags, setFlags] = useState(DEFAULT_FLAG);
  const [identifierColumn, setIdentifierColumn] = useState("handle");
  const [dryRun, setDryRun] = useState(true);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [runTime, setRunTime] = useState(RUN_NOW);
  const [scheduledAt, setScheduledAt] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [downloadingJobId, setDownloadingJobId] = useState(null);
  const [cancellingJobId, setCancellingJobId] = useState(null);
  const [downloadingInputJobId, setDownloadingInputJobId] = useState(null);
  const [restartingJobId, setRestartingJobId] = useState(null);
  const { addToast } = useToast();
  const [showInfoModal, setShowInfoModal] = useState(false);

  const minScheduledAt = formatLocalDatetimeInput(new Date());

  const {
    jobs,
    page,
    totalPages,
    totalFinished,
    setPage,
    refetch: refetchJobs,
  } = useJobsPagination({
    endpoint: `${ENDPOINT}/jobs`,
    cacheKey: "saleFlagsUpdate.jobs",
    pageSize: 10,
  });

  const toolInfo = {
    toolName: "Sale Flags Update",
    description:
      "Add or remove values on the custom.sale_flags product metafield from an uploaded CSV/XLSX. This is the metafield the storefront's final-sale checkout checkbox reads — the tag alone does not fire it. Runs as a background job.",
    steps: [
      "Choose Remove (take a flag off, the sale-end direction) or Add (put one on).",
      "Enter the flag values, comma-separated. 'final-sale' is pre-filled.",
      "Upload a CSV or XLSX and pick which column identifies the product. HANDLE is recommended: the metafield is per product.",
      "Leave Dry run ticked for the first pass. It resolves every product, reads its current flags, and reports the exact before/after list without touching Shopify.",
      "Review the report, then re-run with Dry run unticked to apply it.",
      "Optionally schedule it — the file uploads now and the job fires at the time you pick, so the flag can come off exactly when a sale ends.",
    ],
    templates: [],
  };

  const guideTips = [
    "Why this exists: tagging a product 'final-sale' does not fire the checkout checkbox on its own. The storefront reads the custom.sale_flags metafield through the Storefront API, so a sale has to set both, and ending the sale has to unset both.",
    "Removing the LAST value deletes the metafield rather than writing an empty list. Those are different states — a product that never had the metafield should go back to not having it, not to having an empty one.",
    "A product that keeps other flags is rewritten with just the remainder, so removing 'final-sale' from ['clearance','final-sale'] leaves ['clearance'].",
    "IMPORTANT: this tool strips the flag from every product in the file. Products that carried the value BEFORE the sale must be left alone — so filter them out of the input file rather than relying on the tool. For the 2026 tent sale that means the rows whose recorded 'Sale Flags Before' was '(none)'.",
    "Dry run is the safety net and is ON by default. The report's 'Sale Flags Before' column tells you exactly what each product currently carries.",
    "The metafield is per PRODUCT, not per variant. A variant-level export repeats the same handle many times; duplicate rows are reported as 'duplicate_row' rather than applied again.",
    "Rows already in the desired state come back as 'no_change' — no Shopify write happens, so re-running is safe.",
    "Scheduled jobs are persisted server-side and survive deploys. Cancel a scheduled job before it runs if plans change; once started it runs to completion.",
  ];

  const previousStatusRef = useRef({});
  useEffect(() => {
    const previous = previousStatusRef.current;
    jobs.forEach((j) => {
      const prev = previous[j.job_id];
      if (prev && prev !== j.status) {
        if (j.status === STATUS_DONE) {
          addToast(
            `Sale flags job ${j.job_id} complete. Report is ready.`,
            "success"
          );
        } else if (j.status === STATUS_FAILED) {
          addToast(
            `Sale flags job ${j.job_id} failed: ${j.error || "unknown error"}`,
            "error"
          );
        } else if (j.status === STATUS_CANCELLED) {
          addToast(`Sale flags job ${j.job_id} cancelled.`, "info");
        }
      }
    });
    previousStatusRef.current = Object.fromEntries(
      jobs.map((j) => [j.job_id, j.status])
    );
  }, [jobs, addToast]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!inputFile) {
      addToast("Please upload an input file.", "error");
      return;
    }
    const maxSize = 100 * 1024 * 1024;
    if (inputFile.size > maxSize) {
      addToast("File is too large. Maximum size is 100MB.", "error");
      return;
    }
    const trimmedFlags = flags.trim();
    if (!trimmedFlags) {
      addToast("Enter at least one flag value.", "error");
      return;
    }

    let scheduledIso = null;
    if (runTime === RUN_SCHEDULE) {
      if (!scheduledAt) {
        addToast("Pick a date and time to schedule the job.", "error");
        return;
      }
      const target = new Date(scheduledAt);
      if (Number.isNaN(target.getTime())) {
        addToast("Scheduled date/time is not valid.", "error");
        return;
      }
      if (target.getTime() <= Date.now() + 30 * 1000) {
        addToast(
          "Scheduled date/time must be at least 30 seconds in the future.",
          "error"
        );
        return;
      }
      scheduledIso = target.toISOString();
    }

    // A live run changes storefront behaviour (the final-sale checkbox), so make
    // it an explicit choice.
    if (!dryRun) {
      const ok = window.confirm(
        `LIVE RUN — this will ${action.toUpperCase()} "${trimmedFlags}" ` +
          `${action === ACTION_REMOVE ? "from" : "on"} the custom.sale_flags ` +
          `metafield of the products in "${inputFile.name}".\n\n` +
          (action === ACTION_REMOVE
            ? "Products left with no flags will have the metafield deleted.\n\n"
            : "") +
          "Continue?"
      );
      if (!ok) return;
    }

    const formData = new FormData();
    formData.append("input_file", inputFile);
    formData.append("action", action);
    formData.append("flags", trimmedFlags);
    formData.append("dry_run", dryRun ? "true" : "false");
    if (identifierColumn) {
      formData.append("identifier_column", identifierColumn);
    }
    if (includeArchived) {
      formData.append("include_archived", "true");
    }
    const trimmedSheet = sheetName.trim();
    if (trimmedSheet) {
      formData.append("sheet_name", trimmedSheet);
    }
    if (scheduledIso) {
      formData.append("scheduled_at", scheduledIso);
    }

    setUploading(true);
    setUploadProgress(0);
    try {
      const response = await flaskApi.post(ENDPOINT, formData, {
        onUploadProgress: (evt) => {
          const pct = Math.round((evt.loaded * 100) / (evt.total || 1));
          setUploadProgress(pct);
        },
      });
      const jobId = response.data?.job_id;
      if (!jobId) {
        addToast("Server did not return a job ID.", "error");
        return;
      }
      const modeWord = dryRun ? "Dry-run" : "Live";
      if (scheduledIso) {
        addToast(
          `${modeWord} sale flags job ${jobId} scheduled for ${formatScheduledDisplay(
            scheduledIso
          )}.`,
          "info"
        );
      } else {
        addToast(
          `${modeWord} sale flags job ${jobId} queued. Tracking progress…`,
          "info"
        );
      }
      if (page !== 1) await setPage(1);
      else await refetchJobs();
      setInputFile(null);
      setScheduledAt("");
      setRunTime(RUN_NOW);
    } catch (error) {
      const message = await extractErrorMessage(
        error,
        "Failed to start sale flags job."
      );
      addToast(message, "error");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleCancel = async (job) => {
    if (!job?.job_id) return;
    const ok = window.confirm(
      `Cancel scheduled sale flags job ${job.job_id}? This can't be undone.`
    );
    if (!ok) return;
    setCancellingJobId(job.job_id);
    try {
      await flaskApi.delete(`${ENDPOINT}/jobs/${job.job_id}`);
      addToast(`Job ${job.job_id} cancelled.`, "success");
      await refetchJobs();
    } catch (error) {
      const message = await extractErrorMessage(error, "Failed to cancel job.");
      addToast(message, "error");
    } finally {
      setCancellingJobId(null);
    }
  };

  const handleDownload = async (job) => {
    if (!job?.job_id) return;
    setDownloadingJobId(job.job_id);
    try {
      const response = await flaskApi.get(
        `${ENDPOINT}/jobs/${job.job_id}/download`,
        { responseType: "blob" }
      );
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const contentDisposition =
        response.headers["content-disposition"] ||
        response.headers["Content-Disposition"];
      const extractedFilename = extractFilenameFromHeader(contentDisposition);
      link.setAttribute(
        "download",
        extractedFilename || job.result_filename || "sale_flags_report.xlsx"
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      addToast("Report downloaded.", "success");
    } catch (error) {
      const message = await extractErrorMessage(
        error,
        "Failed to download report."
      );
      addToast(message, "error");
    } finally {
      setDownloadingJobId(null);
    }
  };

  const handleDownloadInput = async (job) => {
    if (!job?.job_id) return;
    setDownloadingInputJobId(job.job_id);
    try {
      const response = await flaskApi.get(
        `${ENDPOINT}/jobs/${job.job_id}/input`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const contentDisposition =
        response.headers["content-disposition"] ||
        response.headers["Content-Disposition"];
      const extractedFilename = extractFilenameFromHeader(contentDisposition);
      link.setAttribute(
        "download",
        extractedFilename || job.input_filename || "input_file"
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      addToast("Input file downloaded.", "success");
    } catch (error) {
      const message = await extractErrorMessage(
        error,
        "Failed to download input file."
      );
      addToast(message, "error");
    } finally {
      setDownloadingInputJobId(null);
    }
  };

  const handleRestart = async (job, scheduledAtLocal) => {
    if (!job?.job_id) return;
    let scheduledIso = null;
    if (scheduledAtLocal) {
      const target = new Date(scheduledAtLocal);
      if (Number.isNaN(target.getTime())) {
        addToast("Scheduled date/time is not valid.", "error");
        return;
      }
      if (target.getTime() <= Date.now() + 30 * 1000) {
        addToast(
          "Scheduled date/time must be at least 30 seconds in the future.",
          "error"
        );
        return;
      }
      scheduledIso = target.toISOString();
    }
    setRestartingJobId(job.job_id);
    try {
      await flaskApi.post(
        `${ENDPOINT}/jobs/${job.job_id}/restart`,
        scheduledIso ? { scheduled_at: scheduledIso } : {}
      );
      addToast(
        scheduledIso
          ? `Sale flags job ${job.job_id} rescheduled for ${formatScheduledDisplay(
              scheduledIso
            )}.`
          : `Sale flags job ${job.job_id} restarted.`,
        scheduledIso ? "info" : "success"
      );
      if (page !== 1) await setPage(1);
      else await refetchJobs();
    } catch (error) {
      const message = await extractErrorMessage(error, "Failed to restart job.");
      addToast(message, "error");
    } finally {
      setRestartingJobId(null);
    }
  };

  return (
    <>
      <ToolPageLayout
        splitLayout
        title="Sale Flags Update"
        subtitle={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
        tips={guideTips}
        onOpenInfo={() => setShowInfoModal(true)}
      >
        <form onSubmit={handleSubmit} className="tool-form">
          <div className="tool-form-main">
            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Action</h3>
              </div>
              <div className="tool-field-stack">
                <div
                  className="tool-radio-group"
                  role="radiogroup"
                  aria-label="Action"
                >
                  <label className="tool-radio-option">
                    <input
                      type="radio"
                      name="sale-flags-action"
                      value={ACTION_REMOVE}
                      checked={action === ACTION_REMOVE}
                      onChange={() => setAction(ACTION_REMOVE)}
                      disabled={uploading}
                    />
                    <span>Remove (take the flag off)</span>
                  </label>
                  <label className="tool-radio-option">
                    <input
                      type="radio"
                      name="sale-flags-action"
                      value={ACTION_ADD}
                      checked={action === ACTION_ADD}
                      onChange={() => setAction(ACTION_ADD)}
                      disabled={uploading}
                    />
                    <span>Add (put the flag on)</span>
                  </label>
                  <span className="tool-helper-text tool-radio-hint">
                    {action === ACTION_REMOVE
                      ? "Only the listed values are removed; any other flags on the product are kept. A product left with no flags has the metafield deleted, restoring the state it had before it was ever flagged."
                      : "Listed values are appended to whatever the product already carries. Products that already have them are reported as no_change."}
                  </span>
                </div>
              </div>
            </section>

            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Flag Values</h3>
              </div>
              <div className="tool-field-stack">
                <label className="tool-label" htmlFor="sale-flags-values">
                  Values on custom.sale_flags
                </label>
                <input
                  id="sale-flags-values"
                  type="text"
                  className="tool-input"
                  placeholder="e.g. final-sale"
                  value={flags}
                  onChange={(event) => setFlags(event.target.value)}
                  disabled={uploading}
                  required
                />
                <span className="tool-helper-text">
                  Comma-separated for more than one. Values are matched exactly
                  and are case-sensitive — "final-sale" is the value the
                  storefront checkout checkbox looks for.
                </span>
              </div>
            </section>

            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Input File</h3>
              </div>
              <ToolFileUploadField
                label="Product list"
                file={inputFile}
                setFile={setInputFile}
                accept=".csv,.xlsx,.xls"
                helperText="CSV or XLSX. Needs the column you pick below — HANDLE, SKU / Parent SKU, SHOPIFY_PRODUCT_ID, or Product Title."
              />
              {action === ACTION_REMOVE ? (
                <span className="tool-helper-text">
                  Include only products that should lose the flag. Products that
                  carried it before the sale need to keep it, so filter those
                  rows out of the file first — the tool has no way to know which
                  is which.
                </span>
              ) : null}
            </section>

            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Identify Products By</h3>
              </div>
              <div className="tool-field-stack">
                <label className="tool-label" htmlFor="sale-flags-identifier">
                  Column
                </label>
                <select
                  id="sale-flags-identifier"
                  className="tool-input"
                  value={identifierColumn}
                  onChange={(event) => setIdentifierColumn(event.target.value)}
                  disabled={uploading}
                >
                  {IDENTIFIER_OPTIONS.map((opt) => (
                    <option key={opt.value || "auto"} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <span className="tool-helper-text">
                  {IDENTIFIER_HELPER[identifierColumn]}
                </span>
              </div>
            </section>

            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">
                  Tab Name{" "}
                  <span className="tool-section-optional">(optional)</span>
                </h3>
              </div>
              <div className="tool-field-stack">
                <label className="tool-label">Excel tab to read</label>
                <p className="tool-helper-text">
                  Only used for .xlsx / .xls files with multiple tabs. Leave blank
                  to use the first tab. Case-sensitive.
                </p>
                <input
                  type="text"
                  className="tool-input"
                  placeholder="e.g. Revert - Sale Flags"
                  value={sheetName}
                  onChange={(event) => setSheetName(event.target.value)}
                  disabled={uploading}
                />
              </div>
            </section>

            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Safety</h3>
              </div>
              <div className="tool-field-stack">
                <label className="shopify-checkbox-label">
                  <input
                    type="checkbox"
                    checked={dryRun}
                    onChange={(event) => setDryRun(event.target.checked)}
                    disabled={uploading}
                  />
                  Dry run — report what would change, without touching Shopify
                </label>
                <span className="tool-helper-text">
                  {dryRun
                    ? "Safe. Every product is resolved and its current flags read, but no change is made."
                    : "LIVE — changes are applied to Shopify. This affects whether the final-sale checkout checkbox appears."}
                </span>
                <label className="shopify-checkbox-label">
                  <input
                    type="checkbox"
                    checked={includeArchived}
                    onChange={(event) =>
                      setIncludeArchived(event.target.checked)
                    }
                    disabled={uploading}
                  />
                  Also match archived products
                </label>
                <span className="tool-helper-text">
                  By default only active products are matched. Draft products are
                  always skipped.
                </span>
              </div>
            </section>

            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">When to Run</h3>
              </div>
              <div className="tool-field-stack">
                <div
                  className="tool-radio-group"
                  role="radiogroup"
                  aria-label="When to run"
                >
                  <label className="tool-radio-option">
                    <input
                      type="radio"
                      name="sale-flags-run-time"
                      value={RUN_NOW}
                      checked={runTime === RUN_NOW}
                      onChange={() => setRunTime(RUN_NOW)}
                      disabled={uploading}
                    />
                    <span>Run now</span>
                  </label>
                  <label className="tool-radio-option">
                    <input
                      type="radio"
                      name="sale-flags-run-time"
                      value={RUN_SCHEDULE}
                      checked={runTime === RUN_SCHEDULE}
                      onChange={() => setRunTime(RUN_SCHEDULE)}
                      disabled={uploading}
                    />
                    <span>Schedule for later</span>
                  </label>
                </div>
                {runTime === RUN_SCHEDULE ? (
                  <>
                    <label
                      className="tool-label"
                      htmlFor="sale-flags-scheduled-at"
                    >
                      Date &amp; time
                    </label>
                    <input
                      id="sale-flags-scheduled-at"
                      type="datetime-local"
                      className="tool-input"
                      value={scheduledAt}
                      min={minScheduledAt}
                      onChange={(event) => setScheduledAt(event.target.value)}
                      disabled={uploading}
                      required
                    />
                    <span className="tool-helper-text">
                      Uses your browser's local time. The file uploads now and the
                      job fires at the scheduled time — handy for taking the
                      final-sale flag off exactly when a sale ends.
                    </span>
                  </>
                ) : null}
              </div>
            </section>

            <div className="tool-action-stack">
              <button
                type="submit"
                className="tool-btn-primary"
                disabled={uploading}
              >
                {uploading
                  ? "Uploading…"
                  : `${runTime === RUN_SCHEDULE ? "Schedule" : "Start"} ${
                      dryRun ? "Dry Run" : `Live ${ACTION_LABEL[action]}`
                    }`}
              </button>
              <ToolProgressPanel
                loading={uploading}
                uploadProgress={uploadProgress}
                uploadMessage="Uploading file…"
                processingMessage="Queueing job…"
              />
            </div>
          </div>
        </form>

        <ToolJobsSection
          title="Your Sale Flags Jobs"
          emptyText="No sale flags jobs yet. Submit a file above to start one."
          jobs={jobs}
          page={page}
          totalPages={totalPages}
          totalFinished={totalFinished}
          onPageChange={setPage}
          renderJob={(j) => {
            const progress = j.progress || {};
            const total = Number(progress.total) || 0;
            const processed = Number(progress.processed) || 0;
            const pct =
              total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;
            const jobAction = j.action === ACTION_ADD ? ACTION_ADD : ACTION_REMOVE;
            // dry_run may be absent on an in-flight doc; progress carries it too.
            const jobDryRun =
              j.dry_run !== undefined ? j.dry_run : progress.dry_run;
            return (
              <div key={j.job_id} className="tool-job-status">
                <div className="tool-job-status-row">
                  <span className="tool-job-label">Job ID</span>
                  <span className="tool-job-value">{j.job_id}</span>
                </div>
                <div className="tool-job-status-row">
                  <span className="tool-job-label">Action</span>
                  <span className="tool-job-value">
                    {ACTION_LABEL[jobAction]}
                    {jobDryRun === false ? " (LIVE)" : jobDryRun ? " (dry run)" : ""}
                  </span>
                </div>
                <div className="tool-job-status-row">
                  <span className="tool-job-label">Flags</span>
                  <span className="tool-job-value">
                    {j.flags || progress.flags || "—"}
                  </span>
                </div>
                <div className="tool-job-status-row">
                  <span className="tool-job-label">Input</span>
                  <span className="tool-job-value">{j.input_filename || "—"}</span>
                </div>
                {j.sheet_name ? (
                  <div className="tool-job-status-row">
                    <span className="tool-job-label">Tab</span>
                    <span className="tool-job-value">{j.sheet_name}</span>
                  </div>
                ) : null}
                {j.scheduled_at ? (
                  <div className="tool-job-status-row">
                    <span className="tool-job-label">Scheduled</span>
                    <span className="tool-job-value">
                      {formatScheduledDisplay(j.scheduled_at)}
                    </span>
                  </div>
                ) : null}
                <div className="tool-job-status-row">
                  <span className="tool-job-label">Status</span>
                  <span className={`tool-job-value tool-job-status-${j.status}`}>
                    {j.status}
                  </span>
                </div>
                {total > 0 ? (
                  <>
                    <div className="tool-job-status-row">
                      <span className="tool-job-label">Progress</span>
                      <span className="tool-job-value">
                        {processed.toLocaleString()} / {total.toLocaleString()} (
                        {pct}%)
                      </span>
                    </div>
                    <div className="progress-bar-container">
                      <div
                        className="progress-bar-fill"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </>
                ) : null}
                <div className="tool-job-counts">
                  <span>Updated: {progress.updated || 0}</span>
                  <span>Would change: {progress.planned || 0}</span>
                  <span>No change: {progress.no_change || 0}</span>
                  <span>Duplicates: {progress.duplicates || 0}</span>
                  <span>Not found: {progress.not_found || 0}</span>
                  <span>Errors: {progress.errors || 0}</span>
                  <span>Skipped: {progress.skipped || 0}</span>
                </div>
                {j.status === STATUS_FAILED && j.error ? (
                  <div className="tool-job-error">Error: {j.error}</div>
                ) : null}
                {j.status === STATUS_DONE ? (
                  <button
                    type="button"
                    className="tool-btn-primary"
                    onClick={() => handleDownload(j)}
                    disabled={downloadingJobId === j.job_id}
                  >
                    {downloadingJobId === j.job_id
                      ? "Downloading…"
                      : "Download Report"}
                  </button>
                ) : null}
                {j.status === STATUS_SCHEDULED ? (
                  <button
                    type="button"
                    className="tool-btn-secondary"
                    onClick={() => handleCancel(j)}
                    disabled={cancellingJobId === j.job_id}
                  >
                    {cancellingJobId === j.job_id ? "Cancelling…" : "Cancel"}
                  </button>
                ) : null}
                <JobInputActions
                  job={j}
                  onDownloadInput={handleDownloadInput}
                  downloadingInput={downloadingInputJobId === j.job_id}
                  onRestart={handleRestart}
                  restarting={restartingJobId === j.job_id}
                  minScheduledAt={minScheduledAt}
                />
              </div>
            );
          }}
        />
      </ToolPageLayout>

      <ToolInfoModal
        isOpen={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        toolName={toolInfo.toolName}
        description={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
      />
    </>
  );
}


export default SaleFlagsUpdateTool;
